import { Typography } from "@mui/material"
import { OreveButton } from "components/button/OreveButton.tsx"
import { useMmsService } from "services/mms/context/MmsServiceContext.tsx"
import { useState, useEffect } from "react"
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined"
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"
import {
    OrevePopup,
    OrevePopupContent,
    OrevePopupTitle,
} from "components/popup/OrevePopup.tsx"
import { useCardHolderContext } from "layouts/card-holder/context/useCardHolderContext.ts"

export const ReactivateCardHolderPopup = () => {
    const {
        openActivateCardHolder: open,
        onClosePopup: handleClose,
        driverSelected: driver,
    } = useCardHolderContext()

    const { cardHolderService } = useMmsService()
    const { useActivateCardHolder } = cardHolderService

    const {
        success,
        isLoading,
        sendRequest: activateDriver,
    } = useActivateCardHolder(driver?.id ?? "")

    const [displayResponse, setDisplayResponse] = useState(false)
    const onActivate = async () => {
        await activateDriver()
        setDisplayResponse(true)
    }

    useEffect(() => {
        if (open) setDisplayResponse(false)
    }, [open])

    if (!driver) return null

    const driverName =
        driver && "name" in driver ? driver.name : driver.licensePlate

    return (
        <OrevePopup open={open} handleClose={() => handleClose()}>
            {displayResponse ? (
                <PopupFeedback
                    name={driverName}
                    success={success ?? false}
                    isLoading={isLoading}
                    handleClose={() => handleClose()}
                />
            ) : (
                <PopupMain
                    name={driverName}
                    isLoading={isLoading}
                    onActivate={onActivate}
                />
            )}
        </OrevePopup>
    )
}

const PopupMain = ({
    name,
    isLoading,
    onActivate,
}: {
    name: string
    isLoading: boolean
    onActivate: () => void
}) => {
    const { isVehicle: isVehicle } = useCardHolderContext()

    return (
        <>
            <OrevePopupTitle>
                <ActivatePopupTitle name={name} isVehicle={isVehicle} />
            </OrevePopupTitle>
            <OrevePopupContent>
                <OreveButton
                    value="Valider"
                    variant="contained"
                    color="primary"
                    loading={isLoading}
                    onClick={onActivate}
                    data-cy={
                        isVehicle
                            ? `vehicle__reactivate--submit`
                            : `driver__reactivate--submit`
                    }
                />
            </OrevePopupContent>
        </>
    )
}

const ActivatePopupTitle = ({
    name,
    isVehicle,
}: {
    name: string
    isVehicle: boolean
}) => {
    return (
        <>
            <CheckCircleOutlinedIcon color="secondary" />
            <Typography
                variant="headlineSmall"
                component="div"
                data-cy={
                    isVehicle
                        ? `vehicle__reactivate--title`
                        : `driver__reactivate--title`
                }
            >
                Réactiver {name}
            </Typography>
        </>
    )
}

const PopupFeedback = ({
    name,
    success,
    isLoading,
    handleClose,
}: {
    name: string
    success: boolean
    isLoading: boolean
    handleClose: () => void
}) => {
    const { isVehicle: isVehicle } = useCardHolderContext()

    return (
        <>
            <OrevePopupTitle>
                {success ? (
                    <CheckCircleOutlinedIcon color="secondary" />
                ) : (
                    <ErrorOutlineIcon color="error" />
                )}
                <Typography
                    variant="headlineSmall"
                    component="div"
                    data-cy={
                        isVehicle
                            ? success
                                ? `vehicle__reactivate--success`
                                : `vehicle__reactivate--error`
                            : success
                              ? `driver__reactivate--success`
                              : `driver__reactivate--error`
                    }
                >
                    {success
                        ? `${name} a été réactivé(e) avec succès`
                        : `${name} n'a pas pu être réactivé(e)`}
                </Typography>
            </OrevePopupTitle>
            <OrevePopupContent>
                <OreveButton
                    value="Retour à la liste"
                    variant="contained"
                    color="primary"
                    loading={isLoading}
                    onClick={() => handleClose()}
                    data-cy={
                        isVehicle
                            ? `vehicle__reactivate--back-to-list`
                            : `driver__reactivate--back-to-list`
                    }
                />
            </OrevePopupContent>
        </>
    )
}
